import { useState } from "react"
import { Link } from "react-router-dom"
import { ArrowLeft, Loader2, Pencil, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { StatusBadge } from "@/components/ui/status-badge"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import { tcmErrorMessage } from "../../api"
import { useSaveTcmResource, useTcmResources } from "../../hooks"
import { RESOURCE_LABELS } from "../../lib/careplan"
import type { TcmResource, TcmResourceCategory } from "../../types"

const ORDER = Object.keys(RESOURCE_LABELS) as TcmResourceCategory[]

/**
 * The clinic's own directory: home health agencies, equipment vendors, pharmacies, rides, the
 * after-hours line. Filled once; each case's needs and the patient's printed plan take their
 * contacts from it unless the case names someone else.
 */
export function TcmResourcesPage() {
  const orgId = useOrganizationsStore((s) => s.selectedOrganizationId)
  const { data = [], isLoading, error } = useTcmResources(orgId)
  const [editing, setEditing] = useState<TcmResource | "new" | null>(null)
  const byCategory = ORDER.map((c) => ({ category: c, rows: data.filter((r) => r.category === c) })).filter((g) => g.rows.length)

  return (
    <div className="mx-auto max-w-4xl space-y-4 px-4 py-4 md:px-6 md:py-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2 h-8 gap-1.5 text-xs text-muted-foreground">
        <Link to="/tcm">
          <ArrowLeft className="h-3.5 w-3.5" />
          Transitional Care
        </Link>
      </Button>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Resource directory</h1>
          <p className="text-sm text-muted-foreground">
            Who your patients call after a hospital stay. The default in each group prints on every patient's plan
            unless a case names someone else.
          </p>
        </div>
        <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setEditing("new")} disabled={!orgId}>
          <Plus className="h-3.5 w-3.5" />
          Add
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">Couldn't load the directory: {tcmErrorMessage(error)}</p>
      ) : byCategory.length === 0 ? (
        <div className="rounded-xl border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
          Nothing here yet. Add your home health agencies, equipment vendor, pharmacy and after-hours line, and they'll
          print on every patient's plan.
        </div>
      ) : (
        <div className="space-y-4">
          {byCategory.map((g) => (
            <section key={g.category} className="space-y-1.5">
              <h2 className="text-[13px] font-semibold">{RESOURCE_LABELS[g.category]}</h2>
              <ul className="divide-y rounded-xl border bg-card">
                {g.rows.map((r) => (
                  <li key={r.id} className="flex items-start gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[13px] font-medium">{r.name}</span>
                        {r.is_default && <StatusBadge variant="success" size="sm">Default</StatusBadge>}
                        {!r.is_active && <StatusBadge variant="muted" size="sm">Not in use</StatusBadge>}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {[r.phone, r.languages.length ? r.languages.join(", ").toUpperCase() : null, r.notes].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => setEditing(r)}>
                      <Pencil className="h-3 w-3" />
                      Edit
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {editing && orgId && <ResourceDialog orgId={orgId} resource={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

function ResourceDialog({ orgId, resource, onClose }: { orgId: string; resource: TcmResource | null; onClose: () => void }) {
  const { toast } = useToast()
  const save = useSaveTcmResource(orgId)
  const [category, setCategory] = useState<TcmResourceCategory>(resource?.category ?? "HOME_HEALTH")
  const [name, setName] = useState(resource?.name ?? "")
  const [phone, setPhone] = useState(resource?.phone ?? "")
  const [notes, setNotes] = useState(resource?.notes ?? "")
  const [languages, setLanguages] = useState<string[]>(resource?.languages ?? [])
  const [isDefault, setIsDefault] = useState(resource?.is_default ?? false)
  const [active, setActive] = useState(resource?.is_active ?? true)

  const submit = () =>
    save.mutate(
      {
        id: resource?.id,
        body: { category, name: name.trim(), phone: phone.trim() || null, notes: notes.trim() || null, languages,
                is_default: isDefault, is_active: active },
      },
      {
        onSuccess: () => {
          toast({ title: "Saved" })
          onClose()
        },
        onError: (err) => toast({ title: "Not saved", description: tcmErrorMessage(err), variant: "destructive" }),
      },
    )

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{resource ? "Edit contact" : "Add a contact"}</DialogTitle>
          <DialogDescription>It prints on the patient's plan, so use the number patients should call.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Group</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as TcmResourceCategory)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORDER.map((c) => (
                  <SelectItem key={c} value={c}>{RESOURCE_LABELS[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="res-name">Name</Label>
              <Input id="res-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="res-phone">Phone</Label>
              <Input id="res-phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Languages spoken</Label>
            <div className="flex gap-3 text-[13px]">
              {(["en", "es", "vi"] as const).map((l) => (
                <label key={l} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={languages.includes(l)}
                    onChange={(e) => setLanguages(e.target.checked ? [...languages, l] : languages.filter((x) => x !== l))}
                  />
                  {l.toUpperCase()}
                </label>
              ))}
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="res-notes">Notes</Label>
            <Textarea id="res-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Fax for orders, hours, areas served…" />
          </div>
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            Default for this group (prints when a case names no one)
          </label>
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            In use
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={save.isPending || !name.trim()}>
            {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
